import { ChangeEvent, FC, Fragment, FormEvent, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Nav, ProgressBar, Row, Tab } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import CaresPageHeader from '../shell/cares-page-header';
import AvailabilitySettingsPanel from './availability-settings-panel';
import CalendarConnectionsPanel from './calendar-connections-panel';
import NotificationPreferencesPanel from './notification-preferences-panel';
import PersonalDisplaySettingsPanel from './personal-display-settings-panel';
import './my-profile-page.scss';
import {
  fetchCurrentUserProfile,
  saveCurrentUserProfile,
  type CurrentUserProfile,
} from './api';

type ProfileFormState = {
  name: string;
  firstName: string;
  lastName: string;
  title: string;
  phone: string;
  timezone: string;
  profilePhoto: File | null;
  removeProfilePhoto: boolean;
};

const emptyFormState: ProfileFormState = {
  name: '',
  firstName: '',
  lastName: '',
  title: '',
  phone: '',
  timezone: '',
  profilePhoto: null,
  removeProfilePhoto: false,
};

type MutableProfileField =
  | 'name'
  | 'firstName'
  | 'lastName'
  | 'title'
  | 'phone'
  | 'timezone'
  | 'removeProfilePhoto';

const timezoneChoices = Array.from(
  new Set([
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'America/Anchorage',
    'Pacific/Honolulu',
    'UTC',
  ].filter(Boolean))
);

const buildFormState = (profile: CurrentUserProfile): ProfileFormState => ({
  name: profile.name ?? '',
  firstName: profile.first_name ?? '',
  lastName: profile.last_name ?? '',
  title: profile.title ?? '',
  phone: profile.phone ?? '',
  timezone: profile.timezone ?? '',
  profilePhoto: null,
  removeProfilePhoto: false,
});

const getInitials = (profile: CurrentUserProfile) => {
  const source = [profile.first_name, profile.last_name, profile.name]
    .filter((value): value is string => Boolean(value && value.trim()))
    .join(' ')
    .trim();

  if (!source) {
    return 'CA';
  }

  return source
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
};

const getCompletionItems = (profile: CurrentUserProfile) => [
  Boolean(profile.name?.trim()),
  Boolean(profile.title?.trim()),
  Boolean(profile.phone?.trim()),
  Boolean(profile.timezone?.trim()),
  profile.companies.length > 0,
  Boolean(profile.avatar_url),
];

const completionPercentage = (profile: CurrentUserProfile) => {
  const items = getCompletionItems(profile);
  const completed = items.filter(Boolean).length;

  return Math.round((completed / items.length) * 100);
};

const accountActions = (profile: CurrentUserProfile) => [
  {
    title: 'Identity provider',
    value: profile.sso_provider ? profile.sso_provider.toUpperCase() : 'Local account',
    note: profile.sso_provider
      ? 'Authentication currently flows through the linked single sign-on provider.'
      : 'No external identity provider is linked to this account yet.',
  },
  {
    title: 'Account status',
    value: profile.status ?? 'unknown',
    note: 'Status is sourced from the current authenticated user record.',
  },
  {
    title: 'Organizations',
    value: `${profile.companies.length}`,
    note: 'Team and department affiliations show where this account can work across CARES.'
  },
];

type ProfileContextCardsProps = {
  profile: CurrentUserProfile;
};

const ProfileContextCards: FC<ProfileContextCardsProps> = ({ profile }) => (
  <Row className="g-3 mt-1 cares-profile-settings-page__context-grid">
    <Col lg={4} className="order-2 order-lg-1">
      <Card className="border custom-card mb-0 h-100">
        <Card.Body>
          <p className="fw-medium mb-2">Team and department affiliations</p>
          <div className="d-flex flex-wrap gap-2">
            {profile.companies.length > 0 ? (
              profile.companies.map((company) => (
                <Badge key={company.id} bg="" className="bg-primary-transparent text-primary">
                  {company.name}
                </Badge>
              ))
            ) : (
              <span className="text-muted fs-13">
                No organization memberships are attached to this account yet.
              </span>
            )}
          </div>
        </Card.Body>
      </Card>
    </Col>
    <Col lg={4} className="order-3 order-lg-2">
      <Card className="border custom-card mb-0 h-100">
        <Card.Body>
          <p className="fw-medium mb-2">Settings hub focus</p>
          <p className="text-muted fs-13 mb-0">
            This CARES-owned page keeps the top profile summary focused on scheduling access and routes self-service settings into one place.
          </p>
        </Card.Body>
      </Card>
    </Col>
    <Col lg={4} className="order-1 order-lg-3">
      <Card className="border custom-card mb-0 h-100">
        <Card.Body>
          <p className="fw-medium mb-2">Next useful action</p>
          <p className="text-muted fs-13 mb-3">
            Complete your editable profile fields first, then use the settings tabs below to jump into scheduling and future calendar connection work.
          </p>
          <Button as={Link} to="/my-schedule/calendar" variant="primary" size="sm">
            Open My Schedule
          </Button>
        </Card.Body>
      </Card>
    </Col>
  </Row>
);

const MyProfilePage: FC = () => {
  const location = useLocation();
  const [profile, setProfile] = useState<CurrentUserProfile | null>(null);
  const [formState, setFormState] = useState<ProfileFormState>(emptyFormState);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('notifications');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedTab = params.get('tab');

    if (
      requestedTab === 'availability'
      || requestedTab === 'notifications'
      || requestedTab === 'integrations'
      || requestedTab === 'display'
      || requestedTab === 'edit-profile'
      || requestedTab === 'account'
    ) {
      setActiveTab(requestedTab);
      return;
    }

    setActiveTab('notifications');
  }, [location.search]);

  useEffect(() => {
    let active = true;

    const loadProfile = async () => {
      try {
        const nextProfile = await fetchCurrentUserProfile();

        if (!active) {
          return;
        }

        setProfile(nextProfile);
        setFormState(buildFormState(nextProfile));
        setLoadError(null);
      } catch (error) {
        if (!active) {
          return;
        }

        setLoadError(error instanceof Error ? error.message : 'Unable to load your profile right now.');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void loadProfile();

    return () => {
      active = false;
    };
  }, []);

  const handleFieldChange = (field: MutableProfileField) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const value = event.target instanceof HTMLInputElement && event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;

      setFormState((current) => ({
        ...current,
        [field]: value,
      }));
    };

  const handlePhotoChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0] ?? null;

    setFormState((current) => ({
      ...current,
      profilePhoto: nextFile,
      removeProfilePhoto: nextFile ? false : current.removeProfilePhoto,
    }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaveError(null);
    setSaveSuccess(null);
    setSaving(true);

    const payload = new FormData();
    payload.append('name', formState.name.trim());
    payload.append('first_name', formState.firstName.trim());
    payload.append('last_name', formState.lastName.trim());
    payload.append('title', formState.title.trim());
    payload.append('phone', formState.phone.trim());
    payload.append('timezone', formState.timezone.trim());
    payload.append('remove_profile_photo', formState.removeProfilePhoto ? '1' : '0');

    if (formState.profilePhoto) {
      payload.append('profile_photo', formState.profilePhoto);
    }

    try {
      const nextProfile = await saveCurrentUserProfile(payload);
      setProfile(nextProfile);
      setFormState(buildFormState(nextProfile));
      setSaveSuccess('Profile details saved.');
      setLoadError(null);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Unable to save your profile right now.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Fragment>
        <CaresPageHeader homepage="User Settings" activepage="User Settings" page="User Settings" actions={<></>} />
        <Card className="custom-card">
          <Card.Body className="p-4">
            <p className="mb-0 text-muted">Loading your settings hub...</p>
          </Card.Body>
        </Card>
      </Fragment>
    );
  }

  if (!profile) {
    return (
      <Fragment>
        <CaresPageHeader homepage="User Settings" activepage="User Settings" page="User Settings" actions={<></>} />
        <Alert variant="danger" className="mb-0">
          {loadError ?? 'Your profile could not be loaded.'}
        </Alert>
      </Fragment>
    );
  }

  const percentComplete = completionPercentage(profile);
  const displayName = profile.name ?? ([profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'CARES User');

  return (
    <Fragment>
      <CaresPageHeader homepage="User Settings" activepage="User Settings" page="User Settings" actions={<></>} />

      {loadError ? (
        <Alert variant="warning">{loadError}</Alert>
      ) : null}

      <div className="cares-profile-settings-page">
        <Row>
          <Col xl={12}>
            <Card className="custom-card cares-page-hero">
              <Card.Body className="p-3 p-xl-4">
                <div className="d-flex flex-column flex-xl-row gap-3 align-items-xl-center justify-content-between">
                  <div className="d-flex flex-column flex-md-row gap-3 align-items-md-center">
                    <div className="lh-1">
                      {profile.avatar_url ? (
                        <span className="avatar avatar-xxl avatar-rounded">
                          <img src={profile.avatar_url} alt={displayName} />
                        </span>
                      ) : (
                        <span className="avatar avatar-xxl avatar-rounded bg-primary text-fixed-white fs-24">
                          {getInitials(profile)}
                        </span>
                      )}
                    </div>
                    <div className="flex-fill">
                      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                        <h4 className="fw-semibold mb-0">{displayName}</h4>
                        <Badge bg="" className="bg-primary-transparent text-primary">
                          {profile.status ?? 'active'}
                        </Badge>
                        {profile.sso_provider ? (
                          <Badge bg="" className="bg-light text-default border">
                            {profile.sso_provider} sign-in
                          </Badge>
                        ) : null}
                      </div>
                      <p className="text-muted mb-2">{profile.title || 'Add a role title to make your profile easier to recognize across CARES.'}</p>
                      <div className="d-flex flex-wrap gap-3 text-muted fs-13">
                        <span><i className="ri-mail-line me-1"></i>{profile.email ?? 'No email on file'}</span>
                        <span><i className="ri-phone-line me-1"></i>{profile.phone || 'Add a phone number'}</span>
                        <span><i className="ri-time-line me-1"></i>{profile.timezone || 'Choose a timezone'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="w-100 w-xl-auto cares-profile-settings-page__completion" style={{ maxWidth: '360px' }}>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="fw-medium">Profile completion</span>
                      <span className="text-muted fs-12">{percentComplete}% ready</span>
                    </div>
                    <ProgressBar now={percentComplete} className="progress-xs mb-3" />
                    <div className="d-flex flex-wrap gap-2">
                      <Badge bg="" className="bg-light text-default border">
                        {profile.companies.length} affiliations
                      </Badge>
                      <Badge bg="" className="bg-light text-default border">
                        {profile.avatar_url ? 'Photo set' : 'Photo needed'}
                      </Badge>
                      <Badge bg="" className="bg-light text-default border">
                        {profile.timezone ? 'Timezone set' : 'Timezone needed'}
                      </Badge>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col xl={12}>
            <Card className="custom-card">
              <Card.Body className="p-0">
                <Tab.Container activeKey={activeTab} onSelect={(eventKey) => setActiveTab(eventKey ?? 'notifications')}>
                  <div className="p-3 border-bottom border-block-end-dashed">
                    <Nav as="ul" variant="pills" className="tab-style-6 gap-2 cares-profile-settings-page__tab-nav">
                      <Nav.Item as="li">
                        <Nav.Link eventKey="display">
                          <i className="ri-palette-line me-1 align-middle d-inline-block"></i>
                          Display
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item as="li">
                        <Nav.Link eventKey="availability">
                          <i className="ri-calendar-check-line me-1 align-middle d-inline-block"></i>
                          Availability
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item as="li">
                        <Nav.Link eventKey="notifications">
                          <i className="ri-notification-3-line me-1 align-middle d-inline-block"></i>
                          Notifications
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item as="li">
                        <Nav.Link eventKey="integrations">
                          <i className="ri-links-line me-1 align-middle d-inline-block"></i>
                          Integrations
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item as="li">
                        <Nav.Link eventKey="edit-profile">
                          <i className="ri-user-settings-line me-1 align-middle d-inline-block"></i>
                          Edit Profile
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item as="li">
                        <Nav.Link eventKey="account">
                          <i className="ri-shield-user-line me-1 align-middle d-inline-block"></i>
                          Account
                        </Nav.Link>
                      </Nav.Item>
                    </Nav>
                  </div>

                  <div className="p-4 cares-profile-settings-page__tab-panel">
                    <Tab.Content>
                      <Tab.Pane eventKey="availability" className="border-0 p-0">
                        <AvailabilitySettingsPanel timezone={profile.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'} />
                      </Tab.Pane>

                      <Tab.Pane eventKey="notifications" className="border-0 p-0">
                        <NotificationPreferencesPanel />
                      </Tab.Pane>

                      <Tab.Pane eventKey="integrations" className="border-0 p-0">
                        <CalendarConnectionsPanel />
                      </Tab.Pane>

                      <Tab.Pane eventKey="display" className="border-0 p-0">
                        <PersonalDisplaySettingsPanel />
                      </Tab.Pane>

                      <Tab.Pane eventKey="edit-profile" className="border-0 p-0">
                        {saveError ? <Alert variant="danger">{saveError}</Alert> : null}
                        {saveSuccess ? <Alert variant="success">{saveSuccess}</Alert> : null}

                        <Form onSubmit={(event) => void handleSubmit(event)}>
                          <Row className="g-3">
                            <Col lg={4}>
                              <Card className="border custom-card mb-0 h-100">
                                <Card.Body>
                                  <h5 className="fw-medium mb-3">Profile media</h5>
                                  <div className="d-flex align-items-center gap-3 mb-3">
                                    {profile.avatar_url ? (
                                      <span className="avatar avatar-xl avatar-rounded">
                                        <img src={profile.avatar_url} alt={displayName} />
                                      </span>
                                    ) : (
                                      <span className="avatar avatar-xl avatar-rounded bg-primary text-fixed-white fs-20">
                                        {getInitials(profile)}
                                      </span>
                                    )}
                                    <div>
                                      <p className="fw-medium mb-1">{displayName}</p>
                                      <p className="text-muted fs-12 mb-0">
                                        Upload a square image up to 2 MB. Saved photos replace any existing local profile photo.
                                      </p>
                                    </div>
                                  </div>
                                  <Form.Group className="mb-3">
                                    <Form.Label>Profile photo</Form.Label>
                                    <Form.Control type="file" accept="image/*" onChange={handlePhotoChange} />
                                    {formState.profilePhoto ? (
                                      <Form.Text className="text-muted">{formState.profilePhoto.name}</Form.Text>
                                    ) : null}
                                  </Form.Group>
                                  <Form.Check
                                    id="remove-profile-photo"
                                    type="checkbox"
                                    label="Remove current photo on save"
                                    checked={formState.removeProfilePhoto}
                                    onChange={handleFieldChange('removeProfilePhoto')}
                                    disabled={!profile.has_local_photo}
                                  />
                                </Card.Body>
                              </Card>
                            </Col>

                            <Col lg={8}>
                              <Card className="border custom-card mb-0">
                                <Card.Body>
                                  <h5 className="fw-medium mb-3">Editable profile details</h5>
                                  <Row className="g-3">
                                    <Col md={6}>
                                      <Form.Group>
                                        <Form.Label>Display name</Form.Label>
                                        <Form.Control
                                          required
                                          type="text"
                                          value={formState.name}
                                          onChange={handleFieldChange('name')}
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Group>
                                        <Form.Label>Role title</Form.Label>
                                        <Form.Control
                                          type="text"
                                          value={formState.title}
                                          onChange={handleFieldChange('title')}
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Group>
                                        <Form.Label>First name</Form.Label>
                                        <Form.Control
                                          type="text"
                                          value={formState.firstName}
                                          onChange={handleFieldChange('firstName')}
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Group>
                                        <Form.Label>Last name</Form.Label>
                                        <Form.Control
                                          type="text"
                                          value={formState.lastName}
                                          onChange={handleFieldChange('lastName')}
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Group>
                                        <Form.Label>Email</Form.Label>
                                        <Form.Control type="email" value={profile.email ?? ''} readOnly />
                                        <Form.Text className="text-muted">
                                          Email stays read-only here because identity data is controlled by the authenticated account source.
                                        </Form.Text>
                                      </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Group>
                                        <Form.Label>Phone</Form.Label>
                                        <Form.Control
                                          type="text"
                                          value={formState.phone}
                                          onChange={handleFieldChange('phone')}
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Group>
                                        <Form.Label>Timezone</Form.Label>
                                        <Form.Select
                                          value={formState.timezone}
                                          onChange={handleFieldChange('timezone')}
                                        >
                                          <option value="">Select timezone</option>
                                          {timezoneChoices.map((choice) => (
                                            <option key={choice} value={choice}>
                                              {choice}
                                            </option>
                                          ))}
                                        </Form.Select>
                                      </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Group>
                                        <Form.Label>Affiliations</Form.Label>
                                        <div className="form-control h-auto d-flex flex-wrap gap-2">
                                          {profile.companies.length > 0 ? (
                                            profile.companies.map((company) => (
                                              <Badge key={company.id} bg="" className="bg-light text-default border">
                                                {company.name}
                                              </Badge>
                                            ))
                                          ) : (
                                            <span className="text-muted fs-13">
                                              Memberships are assigned elsewhere and shown here for context.
                                            </span>
                                          )}
                                        </div>
                                      </Form.Group>
                                    </Col>
                                  </Row>

                                  <div className="d-flex flex-wrap gap-2 mt-4">
                                    <Button type="submit" variant="primary" disabled={saving}>
                                      {saving ? 'Saving...' : 'Save profile'}
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="light"
                                      onClick={() => setFormState(buildFormState(profile))}
                                      disabled={saving}
                                    >
                                      Reset form
                                    </Button>
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                          </Row>
                        </Form>
                      </Tab.Pane>

                      <Tab.Pane eventKey="account" className="border-0 p-0">
                        <Row className="g-3">
                          {accountActions(profile).map((item) => (
                            <Col lg={4} key={item.title}>
                              <Card className="border custom-card mb-0 h-100">
                                <Card.Body>
                                  <p className="text-muted text-uppercase fs-11 mb-2">{item.title}</p>
                                  <h5 className="fw-medium mb-3">{item.value}</h5>
                                  <p className="text-muted fs-13 mb-0">{item.note}</p>
                                </Card.Body>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      </Tab.Pane>
                    </Tab.Content>
                  </div>
                </Tab.Container>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col xl={12}>
            <ProfileContextCards profile={profile} />
          </Col>
        </Row>
      </div>
    </Fragment>
  );
};

export default MyProfilePage;
